import { Badge } from "design-system/atoms/Badge"
import { cn } from "design-system/utils"
import { Hourglass, Tag } from "lucide-react"
import { useTranslation } from "react-i18next"
import { IssueUrgencyEnum, IssueStatusEnum } from "schema"
import { EntityIconTooltip } from "./EntityRow"

type Urgency = (typeof IssueUrgencyEnum)[keyof typeof IssueUrgencyEnum]

const URGENCY_CLASS_NAME = {
  [IssueUrgencyEnum.Low]: "text-neutral",
  [IssueUrgencyEnum.Medium]: "text-support-secondary",
  [IssueUrgencyEnum.High]: "text-support-primary",
} as const

export function IssueUrgencyIdentifier({
  urgency,
  className,
}: {
  urgency: Urgency
  className?: string
}) {
  const { t } = useTranslation()

  return (
    <EntityIconTooltip label={t(`support.entities.urgency.${urgency}`)}>
      <Tag size={14} className={cn(URGENCY_CLASS_NAME[urgency], className)} />
    </EntityIconTooltip>
  )
}

export function IssueUrgencyBadge({
  urgency,
  className,
}: {
  urgency: Urgency
  className?: string
}) {
  const { t } = useTranslation()

  return (
    <Badge
      variant="outline"
      className={cn("shrink-0 border-current", URGENCY_CLASS_NAME[urgency], className)}
    >
      <Tag size={14} />
      <span>{t(`support.entities.urgency.${urgency}`)}</span>
    </Badge>
  )
}

function IssueResolvedBadge({ className }: { className?: string }) {
  const { t } = useTranslation()

  return (
    <Badge variant="outline" className={cn("shrink-0 border-current text-success", className)}>
      <span>{t("support.entities.resolved")}</span>
    </Badge>
  )
}

export function IssueStatusBadge({
  issue,
  className,
}: {
  issue: {
    urgency: Urgency
    status: (typeof IssueStatusEnum)[number]
    qualifiedAt: string | null
  }
  className?: string
}) {
  const { t } = useTranslation()

  if (issue.status === "Closed") {
    return (
      <Badge
        variant="outline"
        className={cn("shrink-0 border-current text-muted-foreground", className)}
      >
        <span>{t("support.entities.closed")}</span>
      </Badge>
    )
  }

  if (issue.status === "Resolved") return <IssueResolvedBadge className={className} />

  if (issue.qualifiedAt === null && issue.status !== "Reopened") {
    return (
      <Badge variant="outline" className={cn("shrink-0 border-current text-chart-1", className)}>
        <Hourglass size={14} />
        <span>{t("support.entities.qualifying")}</span>
      </Badge>
    )
  }

  return <IssueUrgencyBadge urgency={issue.urgency} className={className} />
}
